import { useMemo, type ReactNode } from 'react'
import ReactMarkdown from 'react-markdown'
import styles from './LogAnalysisView.module.css'

type Severity = 'critical' | 'high' | 'medium' | 'low' | 'info'

function detectSeverity(md: string): Severity | null {
  const head = md.slice(0, 1200).toLowerCase()
  if (/критическ/.test(head)) return 'critical'
  if (/высока(я|ой)|high/.test(head)) return 'high'
  if (/средн(яя|ей)|medium/.test(head)) return 'medium'
  if (/низк(ая|ой)|low/.test(head)) return 'low'
  if (/информац|info/.test(head)) return 'info'
  return null
}

const SEVERITY_META: Record<Severity, { label: string; tone: string }> = {
  critical: { label: 'Критическая', tone: 'err' },
  high: { label: 'Высокая', tone: 'err' },
  medium: { label: 'Средняя', tone: 'warn' },
  low: { label: 'Низкая', tone: 'info' },
  info: { label: 'Информация', tone: 'info' },
}

function inlineChildren(children: ReactNode) {
  return children
}

interface Props {
  markdown: string
}

export function LogAnalysisView({ markdown }: Props) {
  const severity = useMemo(() => detectSeverity(markdown), [markdown])
  const meta = severity ? SEVERITY_META[severity] : null

  return (
    <div className={styles.root}>
      {meta ? (
        <div className={[styles.severity, styles[`sev_${meta.tone}`]].join(' ')}>
          <span className={styles.severityLabel}>Критичность</span>
          <span className={styles.severityValue}>{meta.label}</span>
        </div>
      ) : null}

      <div className={styles.md}>
        <ReactMarkdown
          components={{
            h1: ({ children }) => <h3 className={styles.h}>{children}</h3>,
            h2: ({ children }) => <h3 className={styles.h}>{children}</h3>,
            h3: ({ children }) => <h4 className={styles.h4}>{children}</h4>,
            h4: ({ children }) => <h4 className={styles.h4}>{children}</h4>,
            p: ({ children }) => <p className={styles.p}>{children}</p>,
            strong: ({ children }) => <strong className={styles.strong}>{inlineChildren(children)}</strong>,
            em: ({ children }) => <em>{inlineChildren(children)}</em>,
            a: ({ href, children }) => (
              <a className={styles.link} href={href} target="_blank" rel="noreferrer">
                {children}
              </a>
            ),
            ul: ({ children }) => <ul className={styles.ul}>{children}</ul>,
            ol: ({ children }) => <ol className={styles.ol}>{children}</ol>,
            li: ({ children }) => <li className={styles.li}>{children}</li>,
            blockquote: ({ children }) => <blockquote className={styles.quote}>{children}</blockquote>,
            code: ({ className, children }) => {
              const isBlock = Boolean(className)
              if (isBlock) {
                return (
                  <pre className={styles.pre}>
                    <code>{children}</code>
                  </pre>
                )
              }
              return <code className={styles.inlineCode}>{children}</code>
            },
            pre: ({ children }) => <div className={styles.preWrap}>{children}</div>,
            hr: () => <hr className={styles.hr} />,
          }}
        >
          {markdown}
        </ReactMarkdown>
      </div>
    </div>
  )
}
